import React from 'react';
import { useSelector } from 'react-redux';

import { makeSelectAuthorization } from './selectors';

interface AbilityProps {
  children: React.ReactNode;
  renderNoAccess?: () => React.ReactNode;
  access: string;
  isComponent?: boolean;
  /** @deprecated unused — keep for prop compat */
  noAccessAction?: string;
  actions?: string[];
}

interface AuthShape {
  permissions: {
    resource_name: string;
    actions: string[];
    restricted_fields: string[];
    restricted_actions: string[];
  }[];
}

const authSelector = makeSelectAuthorization;

/**
 * `Ability` conditionally renders children based on the user's permissions.
 *
 * Reads `auth.permissions` from Redux and checks whether the resource named
 * in `access` exists and the required actions (defaulting to `['view']`)
 * are all present.
 *
 * - If `isComponent` is false, simply renders children when the resource is
 *   accessible.
 * - If `isComponent` is true, wraps children in a span and verifies that
 *   every action in `actions` is included; otherwise calls `renderNoAccess`.
 *
 * @example
 *   <Ability renderNoAccess={Forbidden} access="rewards">
 *     <Reward />
 *   </Ability>
 *
 * @example
 *   <Ability isComponent renderNoAccess={() => <ForbiddenPage />} access="campaigns">
 *     <CampaignCreatePage />
 *   </Ability>
 */
const Ability: React.FC<AbilityProps> = ({
  children,
  renderNoAccess,
  access,
  isComponent,
  actions
}) => {
  const auth = useSelector(authSelector) as AuthShape;

  // noAccessAction is unused — clean up in subsequent commit
  const requiredActions = actions ? [...actions] : [];
  if (!requiredActions.includes('view')) {
    requiredActions.push('view');
  }

  if (!auth || Object.keys(auth).length === 0) return null;

  const permSec = (auth.permissions || []).find(obj => obj.resource_name === access);
  if (!permSec) {
    return renderNoAccess ? <>{renderNoAccess()}</> : null;
  }

  if (!isComponent) return <>{children}</>;

  if (!('actions' in permSec)) return null;

  if (permSec.actions.length === 0) {
    return renderNoAccess ? <>{renderNoAccess()}</> : null;
  }

  const actionsCheck = requiredActions.every(action => permSec.actions.includes(action));
  if (!actionsCheck) {
    return renderNoAccess ? <>{renderNoAccess()}</> : null;
  }

  return <span style={{ pointerEvents: 'auto' }}>{children}</span>;
};

export default Ability;
